import React, { useMemo, useState } from 'react';
import { t } from '../i18n';
import axios from '../api/axios';
import AdLayoutEditor from './ads/AdLayoutEditor';
import { normalizeContentJson } from './ads/adLayoutDefaults';
import {
  createDefaultInfoCardDesign,
  INFO_CARD_PROFILE_OPTIONS,
  normalizeInfoCardDesign,
  normalizeInfoCardLayout,
} from './infoCardDesignDefaults';

function authHeaders() {
  const token = localStorage.getItem('token') || localStorage.getItem('accessToken') || localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function resolvePreviewUrl(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (/^(https?:\/\/|data:|blob:)/i.test(value)) return value;
  const base = String(axios?.defaults?.baseURL || '').replace(/\/$/, '');
  if (value.startsWith('/')) return base ? `${base}${value}` : value;
  return base ? `${base}/${value}` : value;
}

export default function InfoCardDesignEditor({ value, onChange }) {
  const [device, setDevice] = useState('desktop');
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingElement, setUploadingElement] = useState(false);
  const [error, setError] = useState('');

  const design = useMemo(() => normalizeInfoCardDesign({
    enabled: value?.design_enabled,
    templateKey: value?.design_template_key,
    backgroundMode: value?.design_background_mode,
    imageUrl: value?.design_image_url,
    desktopImageUrl: value?.design_desktop_image_url,
    mobileImageUrl: value?.design_mobile_image_url,
    contentJson: value?.design_content_json,
    layoutJson: value?.design_layout_json,
  }, value?.highlight_point, value?.highlight_message), [value]);

  const patchDesign = (patch) => {
    onChange((previousValue) => {
      const previous = previousValue && typeof previousValue === 'object' ? previousValue : (value || {});
      const previousDesign = normalizeInfoCardDesign({
        enabled: previous?.design_enabled,
        templateKey: previous?.design_template_key,
        backgroundMode: previous?.design_background_mode,
        imageUrl: previous?.design_image_url,
        desktopImageUrl: previous?.design_desktop_image_url,
        mobileImageUrl: previous?.design_mobile_image_url,
        contentJson: previous?.design_content_json,
        layoutJson: previous?.design_layout_json,
      }, previous?.highlight_point, previous?.highlight_message);
      return {
        ...previous,
        design_enabled: patch.enabled ?? previous?.design_enabled ?? previousDesign.enabled,
        design_template_key: patch.templateKey ?? previous?.design_template_key ?? previousDesign.templateKey,
        design_background_mode: patch.backgroundMode ?? previous?.design_background_mode ?? previousDesign.backgroundMode,
        design_image_url: patch.imageUrl ?? previous?.design_image_url ?? previousDesign.imageUrl,
        design_desktop_image_url: patch.desktopImageUrl ?? previous?.design_desktop_image_url ?? previousDesign.desktopImageUrl,
        design_mobile_image_url: patch.mobileImageUrl ?? previous?.design_mobile_image_url ?? previousDesign.mobileImageUrl,
        design_content_json: patch.contentJson ?? previous?.design_content_json ?? previousDesign.contentJson,
        design_layout_json: patch.layoutJson ?? previous?.design_layout_json ?? previousDesign.layoutJson,
      };
    });
  };

  const syncLegacyText = () => {
    const content = normalizeContentJson(design.contentJson);
    const ko = {
      ...(content.translations?.ko || {}),
      title: String(value?.highlight_point || '').trim() || '오늘의 기회!',
      titleAccent: '',
      description: String(value?.highlight_message || '').trim() || '이벤트 안내를 확인하세요.',
    };
    const next = normalizeContentJson({
      ...content,
      translations: { ...content.translations, ko },
      bulkFields: {
        ...content.bulkFields,
        title: `한국어:${ko.title}`,
        titleAccent: '한국어:',
        description: `한국어:${ko.description}`,
      },
    });
    patchDesign({ contentJson: next, enabled: true });
  };

  const resetDesign = () => {
    if (!window.confirm(t('현재 디자인 배치와 다국어 문구를 기본값으로 초기화할까요?'))) return;
    const next = createDefaultInfoCardDesign(value?.highlight_point, value?.highlight_message);
    patchDesign(next);
  };

  const uploadBackground = async (file, targetDevice) => {
    if (!file) return;
    if (!String(file.type || '').startsWith('image/')) return alert(t('이미지 파일만 업로드할 수 있습니다.'));
    setError('');
    setUploadingBg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post('/admin/assets/upload-info-background', fd, { headers: authHeaders() });
      const url = res?.data?.url;
      if (!url) throw new Error('NO_URL');
      if (targetDevice === 'mobile') {
        patchDesign({ mobileImageUrl: url, enabled: true });
      } else {
        // imageUrl도 같이 갱신해 구버전 클라이언트/저장 데이터와 호환한다.
        patchDesign({ desktopImageUrl: url, imageUrl: url, enabled: true });
      }
    } catch (e) {
      console.error('info card background upload failed', e);
      setError(e?.response?.data?.message || t('배경 이미지 업로드에 실패했습니다.'));
    } finally {
      setUploadingBg(false);
    }
  };

  const uploadElementImage = async (file) => {
    if (!file) return null;
    setError('');
    setUploadingElement(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post('/admin/assets/upload-info-element', fd, { headers: authHeaders() });
      const url = res?.data?.url;
      if (!url) throw new Error('NO_URL');
      return {
        id: `info_asset_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        previewUrl: resolvePreviewUrl(url),
        url,
      };
    } catch (e) {
      console.error('info card element upload failed', e);
      setError(e?.response?.data?.message || t('요소 이미지 업로드에 실패했습니다.'));
      return null;
    } finally {
      setUploadingElement(false);
    }
  };

  const assetPreviewUrls = useMemo(() => {
    const out = {};
    const layout = normalizeInfoCardLayout(design.layoutJson);
    (layout.assets || []).forEach((asset) => {
      if (asset?.id && asset?.url) out[asset.id] = resolvePreviewUrl(asset.url);
    });
    return out;
  }, [design.layoutJson]);

  const editorForm = {
    device,
    templateKey: design.templateKey,
    backgroundMode: design.backgroundMode,
    contentJson: design.contentJson,
    layoutJson: design.layoutJson,
  };

  return (
    <div className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50/30 p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-gray-900">{t('이벤트 카드 디자인 · 광고 편집기 방식')}</div>
          <div className="mt-1 text-xs text-gray-600">{t('하단 이벤트 카드 자체를 배경 이미지 + 자유 배치 + 요소 이미지 + 언어별 구분자 텍스트 방식으로 편집합니다.')}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={syncLegacyText} className="rounded border bg-white px-3 py-1.5 text-xs hover:bg-gray-50">{t('기존 하이라이트 문구 불러오기')}</button>
          <button type="button" onClick={resetDesign} className="rounded border bg-white px-3 py-1.5 text-xs hover:bg-gray-50">{t('디자인 초기화')}</button>
        </div>
      </div>

      {error ? <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}

      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-4">
        <label className="flex items-center gap-2 rounded border bg-white px-3 py-2 text-sm">
          <input
            type="checkbox"
            checked={design.enabled}
            onChange={(e) => {
              if (!e.target.checked) {
                patchDesign({ enabled: false });
                return;
              }
              if (!value?.design_enabled) {
                patchDesign(createDefaultInfoCardDesign(value?.highlight_point, value?.highlight_message));
                return;
              }
              patchDesign({ enabled: true });
            }}
          />
          {t('새 디자인 사용')}
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-600">{t('편집 디바이스')}</span>
          <select className="rounded border bg-white px-3 py-2" value={device} onChange={(e) => setDevice(e.target.value)}>
            <option value="desktop">Desktop</option>
            <option value="mobile">Mobile</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-600">{t('렌더링 방식')}</span>
          <select className="rounded border bg-white px-3 py-2" value={design.templateKey} onChange={(e) => patchDesign({ templateKey: e.target.value, enabled: true })}>
            <option value="FREEFORM_V1">{t('배경 + HTML/CSS 요소')}</option>
            <option value="IMAGE_ONLY">{t('이미지만 표시')}</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-600">{t('배경 이미지 맞춤')}</span>
          <select className="rounded border bg-white px-3 py-2" value={design.backgroundMode} onChange={(e) => patchDesign({ backgroundMode: e.target.value, enabled: true })}>
            <option value="COVER">{t('COVER · 영역 채우기')}</option>
            <option value="CONTAIN">{t('CONTAIN · 전체 표시')}</option>
            <option value="FILL">{t('FILL · 강제 맞춤')}</option>
          </select>
        </label>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className="rounded-lg border bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><div className="text-sm font-semibold">{t('Desktop 배경 이미지')}</div><div className="text-[11px] text-gray-500">{t('PC/넓은 화면에서 사용')}</div></div>
            <div className="flex gap-2">
              <label className="cursor-pointer rounded border px-2.5 py-1.5 text-xs hover:bg-gray-50">
                {uploadingBg ? t('업로드 중...') : t('로컬 이미지 업로드')}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden disabled={uploadingBg} onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; uploadBackground(file, 'desktop'); }} />
              </label>
              <button type="button" className="rounded border px-2.5 py-1.5 text-xs hover:bg-gray-50" onClick={() => patchDesign({ desktopImageUrl: '', imageUrl: '', enabled: true })}>{t('배경 제거')}</button>
            </div>
          </div>
          <input className="mt-2 w-full rounded border px-3 py-2 text-sm" value={design.desktopImageUrl || design.imageUrl || ''} onChange={(e) => patchDesign({ desktopImageUrl: e.target.value, imageUrl: e.target.value, enabled: true })} placeholder={t('Desktop 배경 이미지 URL')} />
          {(design.desktopImageUrl || design.imageUrl) ? <div className="mt-2 h-28 overflow-hidden rounded border bg-gray-50"><img src={resolvePreviewUrl(design.desktopImageUrl || design.imageUrl)} alt="desktop event card background" className="h-full w-full object-contain" /></div> : null}
        </div>

        <div className="rounded-lg border bg-white p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><div className="text-sm font-semibold">{t('Mobile 배경 이미지')}</div><div className="text-[11px] text-gray-500">{t('모바일에서 우선 사용 · 비워두면 Desktop 이미지 fallback')}</div></div>
            <div className="flex gap-2">
              <label className="cursor-pointer rounded border px-2.5 py-1.5 text-xs hover:bg-gray-50">
                {uploadingBg ? t('업로드 중...') : t('로컬 이미지 업로드')}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden disabled={uploadingBg} onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; uploadBackground(file, 'mobile'); }} />
              </label>
              <button type="button" className="rounded border px-2.5 py-1.5 text-xs hover:bg-gray-50" onClick={() => patchDesign({ mobileImageUrl: '', enabled: true })}>{t('배경 제거')}</button>
            </div>
          </div>
          <input className="mt-2 w-full rounded border px-3 py-2 text-sm" value={design.mobileImageUrl || ''} onChange={(e) => patchDesign({ mobileImageUrl: e.target.value, enabled: true })} placeholder={t('Mobile 배경 이미지 URL')} />
          {design.mobileImageUrl ? <div className="mt-2 h-28 overflow-hidden rounded border bg-gray-50"><img src={resolvePreviewUrl(design.mobileImageUrl)} alt="mobile event card background" className="h-full w-full object-contain" /></div> : <div className="mt-2 rounded border border-dashed bg-gray-50 px-3 py-5 text-center text-xs text-gray-400">{t('미등록 시 Desktop 이미지를 사용합니다.')}</div>}
        </div>
      </div>

      <div className="mt-3">
        <AdLayoutEditor
          form={editorForm}
          previewUrl={resolvePreviewUrl(device === 'mobile' ? (design.mobileImageUrl || design.desktopImageUrl || design.imageUrl) : (design.desktopImageUrl || design.imageUrl))}
          onChange={(patch) => patchDesign({ ...patch, enabled: true })}
          assetPreviewUrls={assetPreviewUrls}
          onAddElementImage={uploadElementImage}
          onRemoveElementImage={() => {}}
          profileOptions={INFO_CARD_PROFILE_OPTIONS}
        />
      </div>

      {uploadingElement ? <div className="mt-2 text-xs text-indigo-700">{t('요소 이미지 업로드 중...')}</div> : null}
    </div>
  );
}
